/* tslint:disable:curly */
// noinspection RedundantIfStatementJS

import {Component, OnDestroy, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {downloadBlob, errorCode, isDefined, PREMEMBER_ERROR_CODES} from '../../commons/commons';
import {
    AbstractControl,
    AsyncValidatorFn,
    FormBuilder,
    FormControl,
    FormGroup,
    ValidationErrors,
    ValidatorFn,
    Validators
} from '@angular/forms';
import {clearErrors, validateAddressStringLength, validateFloat, validateInteger, validateNotBlank} from '../../commons/validators';
import {
    EMAIL_PATTERN,
    FISCAL_CODE_PATTERN,
    MOBILE_PATTERN,
    NAME_PATTERN,
    POD_PATTERN,
    PROVIDER_PATTERN,
    VAT_NUMBER_PATTERN
} from '../../shared/patterns';
import {ApiService} from '../../api/api.service';
import {debounceTime, map, startWith, switchMap, takeUntil} from 'rxjs/operators';
import {BehaviorSubject, combineLatest, merge, Observable, of, Subject, timer} from 'rxjs';
import {I18NService} from '../../i18n/i18n.service';
import {MsgBoxService} from '../../shared/msg-box.service';
import {NionMessage} from '../../api/dto';
import {
    DEFAULT_MAX_CHARS,
    EMAIL_MAX_CHARS,
    MOBILE_MAX_CHARS,
    ADDRESS_FIELD_MAX_CHARS,
    NAME_MAX_CHARS,
    POD_MAX_CHARS,
    PROFILATIONS_MAX_CHARS,
    TAX_ID_MAX_CHARS,
    TOTAL_CONSUMPTION_MAX_CHARS,
    VAT_NUMBER_MAX_CHARS, DEFAULT_NUMBER_MAX_CHARS
} from '../../shared/limits';
import {SpinnerData} from '../../shared/spinner/spinner.component';
import {DialogService} from '../../shared/dialog/dialog.service';
import {ActivatedRoute, Router} from '@angular/router';
import {ECMemberLegalFormTypePipe, ECMemberSubjectTypePipe, GenericPipe} from '../../shared/shared.pipe';
import {APP_CONFIG} from '../../app.config';
import {
    EC_DOCUMENT_CF,
    EC_DOCUMENT_CI,
    ECEntitySubtype,
    ECJuridicalType,
    ECLegalForm,
    ECMemberAddress,
    ECMemberType,
    ECPodUtilityType,
    ECSubjectType
} from 'smag-commons/cfg/hierarchy';
import {
    AllFieldTypes,
    ConfigurabilityFieldsFrontEnd,
    JURIDICAL_POD_UTILITY_TYPES,
    PHYSICAL_POD_UTILITY_TYPES,
    PreMemberCommunity,
    PreMemberDocument,
    PreMemberDocumentSpec,
    PreMemberLegalDocument,
    PreMemberLegalDocumentSpec,
    PreMemberPersonalData,
    PreMemberPodEnergyProfile,
    PreMemberProfiling,
    PreMemberRequest,
    PreMemberUtility,
    PresentField,
    ReferenceFilters,
    UtilitiesClassConsRanges,
    UtilityProductionType
} from 'pms-commons/premember';
import {AddressExt, getZoneFromProvince,} from '../../shared/google-maps-address-autocomplete/google-maps-address-autocomplete.component';
import {DEFAULT_TIMEZONE} from '../../i18n/i18n-defaults';
import * as moment from 'moment-timezone';
import {Moment} from 'moment-timezone';
import {MatRadioChange} from '@angular/material/radio';
import {MatSelectChange} from '@angular/material/select';
import {Phases, VoltageLevel} from 'smag-commons/cfg';
import {MatCheckboxChange} from '@angular/material/checkbox';
import * as CryptoJS from 'crypto-js';
import {BloomFilter} from 'bloom-filters';
import {StorageService} from 'src/app/commons/storage.service';
import {environment} from '../../../environments/environment';
import {googleCompatibleTextAddress, ITALY, mkMemberAddress} from 'smag-commons/fcn';

interface MimeType {
    name: string;
    type: string;
}

interface TmpDocument {
    fileName: string;
    file: File;
}

// @PoweredByAi(date="2026-07-17", model="claude-opus-4-8")
// ETT-1388: carries the failing document's name so the user is told exactly which upload failed.
class DocumentUploadError extends Error {
    constructor(readonly fileName: string, readonly cause: any) {
        super(`Failed to upload document ${fileName}`);
        // keep instanceof working even when TypeScript downlevels to ES5
        Object.setPrototypeOf(this, DocumentUploadError.prototype);
    }
}

const REPLICA_POD_DOC_PLACEHOLDER = '###POD###';

const PERSONAL_DATA = 'Personal data';
const YOUR_UTILITIES = 'Your utilities';
const ENERGY_PROFILING = 'Energy profiling';
const UPLOAD_DOCUMENTS = 'Upload documents';
const ADDITIONAL_DOCUMENTS = 'Consents';

const ALLOWED_COUNTRIES = [ITALY];

const ENERGY_PROFILES = [
    PreMemberPodEnergyProfile.PROSUMER,
    PreMemberPodEnergyProfile.CONSUMER,
    PreMemberPodEnergyProfile.ASPIRING_PROSUMER,
    PreMemberPodEnergyProfile.PRODUCER
];

const DEFAULT_PHASES = Phases.SINGLE;
const DEFAULT_VOLTAGE = VoltageLevel.LOW;

/* Steps will comprehend the current existing steps, taken from the backend configuration;
*  the missing fields are going to be managed somewhere else
*/
// tslint:disable-next-line:prefer-const
const STEPS: string[] = [
    PERSONAL_DATA, YOUR_UTILITIES, ENERGY_PROFILING, UPLOAD_DOCUMENTS, ADDITIONAL_DOCUMENTS
];

interface ConfigFieldsContainer {
    stepPersonalData: {
        subject_type: PresentField;  // this includes juridical type, legal form, subject type, entity subtype
        first_name: PresentField;
        last_name: PresentField;
        company_name: PresentField;
        address: PresentField;
        tax_id: PresentField;
        email: PresentField;
        pec: PresentField;
        piva: PresentField;
        mobile_number: PresentField;
    };
    stepUtilities: {
        pod: PresentField;
        pod_utility_type: PresentField;
        pod_address: PresentField;
        wifi: PresentField;
        energy_profile: PresentField;
        utility_production_type: PresentField;
        plant_nominal_power: PresentField;
        plant_effective_date: PresentField;
        provider: PresentField;
        yearly_equivalent_hours: PresentField;
        phases: PresentField;
        voltage: PresentField;
        plant_incentive: PresentField;
    };
}

const MAX_FILE_MB = 2;

// the backend stores the document filename in a varchar(64) column, so the name we send must
// not exceed this length. See limitFileName below.
const MAX_FILE_NAME_LENGTH = 64;

/**
 * @PoweredByAi(date="2026-07-24", model="claude-opus-4-8")
 * ETT-1388: cap a filename to `max` characters, preserving its extension where possible, so a legitimate
 * upload is never blocked (nor the DB insert broken) by a purely cosmetic over-long filename.
 * Exported as a pure function so it can be unit-tested in isolation.
 */
export function limitFileName(name: string, max: number = MAX_FILE_NAME_LENGTH): string {
    if (!name || name.length <= max) {
        return name;
    }
    const dot = name.lastIndexOf('.');
    // keep the extension only if it looks like a real one (present, short, not the whole name)
    if (dot > 0 && name.length - dot <= 10) {
        const ext = name.slice(dot);
        if (ext.length < max) {
            return name.slice(0, max - ext.length) + ext;
        }
    }
    return name.slice(0, max);
}

const FILE_MIME_TYPES: MimeType[] = [
    {
        type: 'application/pdf',
        name: 'PDF'
    },
    {
        type: 'image/jpeg',
        name: 'JPG'
    },
    {
        type: 'image/png',
        name: 'PNG'
    }
];

const DIALOG_WIDTH = 700;
const SHOW_BREADCRUMB = APP_CONFIG.showBreadCrumb;
const PROSPECTUS_TITLE = APP_CONFIG.prospectusTitle;
const PROSPECTUS_BODY = APP_CONFIG.prospectusBody;
const CONDOMINIUM = 'condominium';
const SME_FACTORY = 'sme_factory';
const RESIDENTIAL = 'residential';

const LEGAL_FORM_TYPES: ECLegalForm[] = [
    ECLegalForm.ENTREPRENEUR_SOLE_PROPRIETORSHIP_AGRICULTURAL_ENTERPRISE,
    ECLegalForm.OTHER_INDIVIDUALS
];

const SUBJECT_TYPES: ECSubjectType[] = [
    ECSubjectType.TERRITORIAL_BODY_LOCAL_AUTHORITY_OTHER_BODY,
    ECSubjectType.PMI,
    ECSubjectType.ASSOCIATION_WITH_LEGAL_PERSONALITY_UNDER_PRIVATE_LAW,
    ECSubjectType.ASSOCIATED_FIRM_COMPANY_OF_PROFESSIONALS,
    ECSubjectType.OTHER,
    // ECSubjectType.COMMUNITY  // unnecessary with members
];

const ENTITY_SUBTYPES: ECEntitySubtype[] = [
    ECEntitySubtype.STATE,
    ECEntitySubtype.REGION,
    ECEntitySubtype.PROVINCE,
    ECEntitySubtype.MUNICIPALITY,
    ECEntitySubtype.MUNICIPALITIES_UNION,
    ECEntitySubtype.MOUNTAIN_COMMUNITY,
    ECEntitySubtype.ISLAND_COMMUNITY,
    ECEntitySubtype.METROPOLITAN_CITY,
    ECEntitySubtype.RESEARCH_AND_TRAINING_ORGANIZATION,
    ECEntitySubtype.RELIGIOUS_ORGANIZATION,
    ECEntitySubtype.THIRD_SECTOR_ORGANIZATION,
    ECEntitySubtype.ENVIRONMENT_PREVENTION_ORGANIZATION,
    ECEntitySubtype.LOCAL_ADMINISTRATION_ORGANIZATION
];

const PHASES = [
    Phases.SINGLE,
    Phases.THREE
];

const VOLTAGE_LEVELS = [
    VoltageLevel.LOW,
    VoltageLevel.MEDIUM
];

interface PreMemberDocumentType {
    document_type: string;
    optional: boolean;
    pod?: string; // set if this document is POD-specific
    template_id?: string;
}

const COMMUNITY_ID_URL_PARAM = 'communityId';

@Component({
    selector: 'app-application-form',
    templateUrl: './application-form.component.html',
    styleUrls: ['./application-form.component.scss']
})
export class ApplicationFormComponent implements OnInit, OnDestroy {
    private readonly destroyed = new Subject<void>();
    private readonly pipe = new GenericPipe(this.i18n);
    private readonly legalFormPipe = new ECMemberLegalFormTypePipe();
    private readonly subjectTypePipe = new ECMemberSubjectTypePipe();
    private readonly podAddresses: { [podId: string]: ECMemberAddress } = {};
    private readonly STEPS: string[] = [];
    private presentFields: PresentField[] = null;
    private tmpDocuments: { [id: string]: TmpDocument } = {};
    // ETT-1388: staging token reused across submit retries, and the files already staged under it
    private stagingToken: string = null;
    private stagedDocuments: { [documentType: string]: File } = {};
    private selectedJuridicalType: ECJuridicalType = ECJuridicalType.INDIVIDUAL;
    private podsIds: string[];
    private turboMaxHeight: number;
    private memberAddress: ECMemberAddress = null;
    private energyProfilingMandatory = false;
    private docSpecs: PreMemberDocumentSpec[] = [];
    private communityId: string;
    @ViewChild('spinner') private spinnerDialog: TemplateRef<any>;
    @ViewChild('setPlantDate') private setPlantDateDialog: TemplateRef<any>;
    personalDataForm: FormGroup;
    utilitiesForm: FormGroup;
    profilationsForm: FormGroup;
    legalDocumentsForm: FormGroup;
    currentStep = 0;
    docTypes: PreMemberDocumentType[] = [];
    legalDocTypes: PreMemberLegalDocumentSpec[];
    pods: string[] = [];
    ecmLinked = false;
    instanceCommunities: PreMemberCommunity[] | null;
    configurabilitySteps: ConfigFieldsContainer;
    showFormHeaderMessage = false;
    aspiringProsumerEnabled = false;
    communityFromUrl: PreMemberCommunity | undefined;

    // Exported in HTML
    readonly ACCEPTED_MIME_TYPES = FILE_MIME_TYPES.map(mt => mt.type).join(',');
    readonly MAX_FILE_MB = MAX_FILE_MB;
    readonly ECJuridicalType = ECJuridicalType;
    readonly PreMemberPodEnergyProfile = PreMemberPodEnergyProfile;
    readonly ECSubjectType = ECSubjectType;
    readonly UtilityProductionType = UtilityProductionType;
    readonly UtilitiesClassConsRanges = UtilitiesClassConsRanges;
    readonly DEFAULT_MAX_CHARS = DEFAULT_MAX_CHARS;
    readonly DEFAULT_NUMBER_MAX_CHARS = DEFAULT_NUMBER_MAX_CHARS;
    readonly POD_MAX_CHARS = POD_MAX_CHARS;
    readonly NAME_MAX_CHARS = NAME_MAX_CHARS;
    readonly EMAIL_MAX_CHARS = EMAIL_MAX_CHARS;
    readonly MOBILE_MAX_CHARS = MOBILE_MAX_CHARS;
    readonly TAX_ID_MAX_CHARS = TAX_ID_MAX_CHARS;
    readonly TOTAL_CONSUMPTION_MAX_CHARS = TOTAL_CONSUMPTION_MAX_CHARS;
    readonly PROFILATIONS_MAX_CHARS = PROFILATIONS_MAX_CHARS;
    readonly VAT_NUMBER_MAX_CHARS = VAT_NUMBER_MAX_CHARS;
    readonly ADDRESS_FIELD_MAX_CHARS = ADDRESS_FIELD_MAX_CHARS;
    readonly SHOW_BREADCRUMB = SHOW_BREADCRUMB;
    readonly PROSPECTUS_BODY = PROSPECTUS_BODY;
    readonly PROSPECTUS_TITLE = PROSPECTUS_TITLE;
    readonly CONDOMINIUM = CONDOMINIUM;
    readonly SME_FACTORY = SME_FACTORY;
    readonly RESIDENTIAL = RESIDENTIAL;
    readonly legalFormTypes: ECLegalForm[] = LEGAL_FORM_TYPES;
    readonly subjectTypes: ECSubjectType[] = SUBJECT_TYPES;
    readonly entitySubtypes: ECEntitySubtype[] = ENTITY_SUBTYPES;
    readonly PHASES = PHASES;
    readonly VOLTAGE_LEVELS = VOLTAGE_LEVELS;
    // Manage steps configurability in HTML
    readonly PERSONAL_DATA = PERSONAL_DATA;
    readonly YOUR_UTILITIES = YOUR_UTILITIES;
    readonly ENERGY_PROFILING = ENERGY_PROFILING;
    readonly UPLOAD_DOCUMENTS = UPLOAD_DOCUMENTS;
    readonly ADDITIONAL_DOCUMENTS = ADDITIONAL_DOCUMENTS;
    readonly isDefined = isDefined;


    constructor(private readonly fb: FormBuilder,
                private readonly api: ApiService,
                private readonly msgBoxService: MsgBoxService,
                private readonly i18n: I18NService,
                private readonly dialogService: DialogService,
                private readonly router: Router,
                private readonly storageService: StorageService,
                private readonly route: ActivatedRoute) {
    }

    ngOnInit() {
        // get community id from url
        this.communityId = this.route.snapshot.queryParamMap.get(COMMUNITY_ID_URL_PARAM);

        combineLatest([
            this.getFieldsConfigurability(),
            this.isLinkedToEcm(),
            this.getInstanceCommunities(),
            this.api.getDocTypes(),
            this.api.getLegalDocTypes()
        ]).pipe(takeUntil(this.destroyed))
            .subscribe(([fieldsCfg, isLinked, communities, docSpecs, legalDocTypes]) => {
                this.presentFields = fieldsCfg.present_fields;
                this.aspiringProsumerEnabled = fieldsCfg.aspiring_prosumer_enabled;
                this.showFormHeaderMessage = !fieldsCfg.is_form_header_message_missing;
                this.ecmLinked = isLinked;
                this.instanceCommunities = communities; // null if not configured
                // get actual community from url retrieved id
                if (this.instanceCommunities) {
                    this.communityFromUrl = this.instanceCommunities.find(c => c.id === this.communityId);
                }
                this.docSpecs = docSpecs;
                this.legalDocTypes = legalDocTypes;
                this.energyProfilingMandatory = fieldsCfg.is_energy_profiling_step_mandatory;

                this.setMissingFieldsConfigurability();
                let steps = fieldsCfg.is_energy_profiling_step_missing ?
                    STEPS.filter(step => step !== ENERGY_PROFILING) :
                    STEPS;
                // NOTE: remove "upload documents" step altogether if no documents are *ever* possible:
                //       if, under some conditions, no documents actually become available among the possible ones,
                //       then a banner is shown
                steps = !docSpecs.length ?
                    steps.filter(step => step !== UPLOAD_DOCUMENTS) :
                    steps;
                steps.forEach(t => this.STEPS.push(t));
                for (const step of this.STEPS) {
                    switch (step) {
                        case PERSONAL_DATA:
                            this.personalDataForm = this.fb.group({
                                juridicalType: new FormControl(ECJuridicalType.INDIVIDUAL,
                                    this.configurabilitySteps.stepPersonalData.subject_type ? [validateIfMandatory(this.configurabilitySteps.stepPersonalData.subject_type, Validators.required)] : []),
                                legalForm: new FormControl(null, []),
                                subjectType: new FormControl(null, []),
                                entitySubtype: new FormControl(null, []),
                                firstName: new FormControl(null,
                                    this.configurabilitySteps.stepPersonalData.first_name ? [validateIfMandatory(this.configurabilitySteps.stepPersonalData.first_name, validateNotBlank), Validators.pattern(NAME_PATTERN), Validators.maxLength(NAME_MAX_CHARS)] : []),
                                lastName: new FormControl(null,
                                    this.configurabilitySteps.stepPersonalData.last_name ? [validateIfMandatory(this.configurabilitySteps.stepPersonalData.last_name, validateNotBlank), Validators.pattern(NAME_PATTERN), Validators.maxLength(NAME_MAX_CHARS)] : []),
                                address: new FormControl(null,
                                    this.configurabilitySteps.stepPersonalData.address ? [validateIfMandatory(this.configurabilitySteps.stepPersonalData.address, Validators.required), validateAddressStringLength(() => isDefined(this.memberAddress), ADDRESS_FIELD_MAX_CHARS)] : [validateAddressStringLength(() => isDefined(this.memberAddress), ADDRESS_FIELD_MAX_CHARS)]),
                                email: new FormControl(null,
                                    this.configurabilitySteps.stepPersonalData.email ?
                                        [validateIfMandatory(this.configurabilitySteps.stepPersonalData.email, validateNotBlank), Validators.pattern(EMAIL_PATTERN), Validators.maxLength(EMAIL_MAX_CHARS)] :
                                        [],
                                    this.emailAsyncValidator()),
                                confirmEmail: new FormControl(null,
                                    this.configurabilitySteps.stepPersonalData.email ? [validateIfMandatory(this.configurabilitySteps.stepPersonalData.email, validateNotBlank), Validators.pattern(EMAIL_PATTERN), Validators.maxLength(EMAIL_MAX_CHARS)] : []),
                                pec: new FormControl(null,
                                    this.configurabilitySteps.stepPersonalData.pec ? [validateIfMandatory(this.configurabilitySteps.stepPersonalData.pec, validateNotBlank), Validators.pattern(EMAIL_PATTERN), Validators.maxLength(EMAIL_MAX_CHARS)] : []),
                                // ETT-1417: mobile numbers are allowed to be non-unique, so the async
                                // uniqueness validator (mobileAsyncValidator) is no longer applied here.
                                mobile: new FormControl(null,
                                    this.configurabilitySteps.stepPersonalData.mobile_number ?
                                        [validateIfMandatory(this.configurabilitySteps.stepPersonalData.mobile_number, validateNotBlank), Validators.pattern(MOBILE_PATTERN), Validators.maxLength(MOBILE_MAX_CHARS)] :
                                        []),
                                companyName: new FormControl(null,
                                    this.configurabilitySteps.stepPersonalData.company_name ? [validateIfMandatory(this.configurabilitySteps.stepPersonalData.company_name, validateNotBlank), Validators.maxLength(DEFAULT_MAX_CHARS)] : []),
                                vatNumber: new FormControl(null,
                                    this.configurabilitySteps.stepPersonalData.piva ? [validateIfMandatory(this.configurabilitySteps.stepPersonalData.piva, validateNotBlank), Validators.pattern(VAT_NUMBER_PATTERN), Validators.maxLength(VAT_NUMBER_MAX_CHARS)] : []),
                                taxCode: new FormControl(null,
                                    this.configurabilitySteps.stepPersonalData.tax_id ? [validateIfMandatory(this.configurabilitySteps.stepPersonalData.tax_id, validateNotBlank), Validators.pattern(FISCAL_CODE_PATTERN), Validators.maxLength(TAX_ID_MAX_CHARS)] : []),
                                preferredCommunity: new FormControl(null, [])
                            }, {
                                validators: ctrl => this.validateForm(ctrl)
                            });

                            this.personalDataForm.valueChanges
                                .pipe(takeUntil(this.destroyed))
                                .subscribe(() => this.updateDocTypes());
                            break;
                        case YOUR_UTILITIES:
                            this.utilitiesForm = this.fb.group({ other: [null] });
                            this.utilitiesForm.valueChanges
                                .pipe(takeUntil(this.destroyed))
                                .subscribe(() => this.updateDocTypes());
                            break;
                        case ENERGY_PROFILING:
                            this.profilationsForm = this.fb.group({});
                            break;
                        case UPLOAD_DOCUMENTS:
                            break;
                        case ADDITIONAL_DOCUMENTS:
                            this.legalDocumentsForm = this.fb.group({});
                            this.legalDocTypes.forEach(dt => this.legalDocumentsForm
                                .addControl(dt.document_type, new FormControl(false, dt.optional ? [] : [Validators.requiredTrue])));
                            break;
                    }
                }
                this.addAnotherPod();

                // listen to subject juridical type value changes
                this.personalDataForm.controls.juridicalType.valueChanges.pipe(
                    startWith(this.personalDataForm.controls.juridicalType.value as ECJuridicalType),  // force start
                    takeUntil(this.destroyed)
                ).subscribe(selectedJuridicalType => {
                    this.selectedJuridicalType = selectedJuridicalType;
                    this.personalDataForm.controls.subjectType.reset();
                    this.onChangeSubjectType(selectedJuridicalType);
                    this.onChangeResetPodsVals();
                    this.profilationsForm?.reset();
                });

                // listen to subject type value changes
                this.personalDataForm.controls.subjectType.valueChanges
                    .pipe(takeUntil(this.destroyed))
                    .subscribe(selectedSubjType => {
                        if (selectedSubjType === ECSubjectType.TERRITORIAL_BODY_LOCAL_AUTHORITY_OTHER_BODY) {
                            this.personalDataForm.controls.entitySubtype.setValidators([Validators.required]);
                        } else {
                            this.personalDataForm.controls.entitySubtype.clearValidators();
                            this.personalDataForm.controls.entitySubtype.updateValueAndValidity();
                        }
                        this.onChangeSubjectType(this.selectedJuridicalType);
                        this.onChangeResetPodsVals();
                        this.profilationsForm?.reset();
                    });

                // listen to legal form value change to reset: company name, tax code and vat number
                this.personalDataForm.controls.legalForm.valueChanges
                    .pipe(takeUntil(this.destroyed))
                    .subscribe(() => {
                        this.personalDataForm.controls.companyName.reset();
                        this.personalDataForm.controls.taxCode.reset();
                        this.personalDataForm.controls.vatNumber.reset();
                        this.onChangeResetPodsVals();
                        this.profilationsForm?.reset();
                        this.updateLimitHeight();
                    });

                this.personalDataForm.controls.entitySubtype.valueChanges.pipe(
                    takeUntil(this.destroyed))
                    .subscribe(() => {
                        this.onChangeResetPodsVals();
                        this.profilationsForm?.reset();
                    });

                if (communities) { // add neutral obj to arr
                    this.instanceCommunities.unshift({
                        id: '0',
                        name: 'No preference'
                    });
                    this.personalDataForm.controls.preferredCommunity.setValue(this.communityFromUrl ? this.communityFromUrl : this.instanceCommunities[0]);
                }

                this.updateDocTypes();
            });
        this.getReference().then();
    }

    ngOnDestroy() {
        this.destroyed.next();
        this.destroyed.complete();
    }

    get steps(): string[] {
        // even if enabled at configuration level, profiling step is hidden if *all* PODs are producers
        if (this.pods.every(pod => !!this.utilitiesForm &&
            this.utilitiesForm.controls[`pod_${pod}_energy_profile`].value === PreMemberPodEnergyProfile.PRODUCER)) {
            return this.STEPS.filter(step => step !== ENERGY_PROFILING);
        }
        return this.STEPS;
    }

    get podsWithProfilation(): string[] {
        return this.pods.filter(pod => this.utilitiesForm.controls[`pod_${pod}_energy_profile`].value !== PreMemberPodEnergyProfile.PRODUCER);
    }

    get energyProfiles(): PreMemberPodEnergyProfile[] {
        return ENERGY_PROFILES.filter(profile => profile !== PreMemberPodEnergyProfile.ASPIRING_PROSUMER || this.aspiringProsumerEnabled);
    }

    get addressFormControl(): FormControl {
        return this.personalDataForm.controls.address as FormControl;
    }

    get mimeTypesNames(): string {
        return FILE_MIME_TYPES.map(mt => mt.name).join(', ');
    }

    get isPersonalDataPageValid(): boolean {
        let valid =
            this.personalDataForm.controls.juridicalType.valid &&
            this.personalDataForm.controls.legalForm.valid &&
            this.personalDataForm.controls.subjectType.valid &&
            this.personalDataForm.controls.entitySubtype.valid &&
            this.personalDataForm.controls.firstName.valid &&
            this.personalDataForm.controls.lastName.valid &&
            this.personalDataForm.controls.address.valid &&
            this.personalDataForm.controls.email.valid &&
            this.personalDataForm.controls.confirmEmail.valid &&
            this.personalDataForm.controls.pec.valid &&
            this.personalDataForm.controls.mobile.valid;
        if (this.isCompany) {
            valid = valid && this.personalDataForm.controls.companyName.valid &&
                this.personalDataForm.controls.vatNumber.valid;
        }
        if (!this.isCompany) {
            valid = valid && this.personalDataForm.controls.taxCode.valid;
        }
        // Form debugging (you can copy and paste into other form controls -change formName- to check them)
        // console.debug('form is valid --> ', this.personalDataForm.valid);
        // Object.keys(this.personalDataForm.controls).forEach(key => {
        //     const control = this.personalDataForm.get(key);
        //     console.log('Controllo:', key, ', Valore:', control.value, ', Validità:', control.valid);
        // });
        return valid;
    }

    get isYourUtilitiesPageValid(): boolean {
        let valid = true;
        this.pods.forEach(pod => {
            valid = valid &&
                this.utilitiesForm.controls[`pod_${pod}_code`].valid &&
                (this.utilitiesForm.controls[`pod_${pod}_pod_utility_type`].valid || this.utilitiesForm.controls[`pod_${pod}_pod_utility_type`].disabled) && // can be disabled (e.g. for producers)
                this.utilitiesForm.controls[`pod_${pod}_wifi`].valid &&
                this.utilitiesForm.controls[`pod_${pod}_energy_profile`].valid &&
                this.utilitiesForm.controls[`pod_${pod}_provider`].valid &&
                this.utilitiesForm.controls[`pod_${pod}_phases`].valid &&
                this.utilitiesForm.controls[`pod_${pod}_voltage`].valid;
            if (this.hasPlant(this.utilitiesForm.controls[`pod_${pod}_energy_profile`].value)) {
                valid = valid &&
                    this.utilitiesForm.controls[`pod_${pod}_plant_type`].valid &&
                    this.utilitiesForm.controls[`pod_${pod}_plant_nominal_power`].valid &&
                    (this.utilitiesForm.controls[`pod_${pod}_yearly_equivalent_hours`].valid || this.utilitiesForm.controls[`pod_${pod}_yearly_equivalent_hours`].disabled) && // can be disabled (e.g. for photovoltaic)
                    this.validatePlantEffectiveDate(pod) &&
                    this.utilitiesForm.controls[`pod_${pod}_plant_has_incentive`].valid &&
                    this.utilitiesForm.controls[`pod_${pod}_plant_incentive`].valid;
            }
            if (!this.addressPodFormControl(pod).disabled) {
                valid = valid && this.utilitiesForm.controls[`pod_${pod}_address`].valid;
            }
        });
        return valid;
    }

    get isEnergyProfilingPageValid(): boolean {
        let valid = true;
        this.pods.forEach(pod => {
            const energyProfile = this.utilitiesForm.controls[`pod_${pod}_energy_profile`].value;
            if (energyProfile === PreMemberPodEnergyProfile.PRODUCER) {
                // skip profiling check for producer PODs
                return;
            }
            valid = valid && this.profilationsForm.controls[`pod_${pod}_yearly_consumption`].valid;
            if (!this.isCompany) {
                valid = valid &&
                    this.profilationsForm.controls[`pod_${pod}_people`].valid &&
                    this.profilationsForm.controls[`pod_${pod}_tv`].valid &&
                    this.profilationsForm.controls[`pod_${pod}_pc`].valid &&
                    this.profilationsForm.controls[`pod_${pod}_ac`].valid &&
                    this.profilationsForm.controls[`pod_${pod}_dishwasher`].valid;
            }
            valid = valid && this.validateYearlyConsumption(pod);
        });
        return valid;
    }

    // This gives you the max needed height to let divs fall into the fxLayout "column wrap" without creating a third column
    get limitColumnHeight(): string {
        const len: number = Math.floor(this.turboMaxHeight / 2) + this.turboMaxHeight % 2;
        return ((100 * len) + 152).toString();
    }

    get isCompany(): boolean {
        return !(this.selectedJuridicalType === ECJuridicalType.INDIVIDUAL &&
            (this.personalDataForm?.controls.legalForm.value === ECLegalForm.OTHER_INDIVIDUALS ||
                this.personalDataForm?.controls.legalForm.value !== ECLegalForm.ENTREPRENEUR_SOLE_PROPRIETORSHIP_AGRICULTURAL_ENTERPRISE));
    }

    isStepEnabled(stepIndex: number): boolean {
        return this.currentStep >= stepIndex;
    }

    getFieldsConfigurability(): Observable<ConfigurabilityFieldsFrontEnd> {
        return this.api.getFieldsConfigurability();
    }

    isLinkedToEcm(): Observable<boolean> {
        return this.api.isLinkedToEcm();
    }

    getInstanceCommunities(): Observable<PreMemberCommunity[] | null> {
        return this.api.getInstanceCommunities();
    }

    addressPodFormControl(podId: string): FormControl {
        return this.utilitiesForm.controls['pod_' + podId + '_address'] as FormControl;
    }

    secondPartPodValue(podId: string): boolean {
        return this.profilationsForm.controls['pod_' + podId + '_second_part'].value;
    }

    estimatedYearlyConsForPod(podId: string): string {
        return this.profilationsForm.controls['pod_' + podId + '_estimated_yearly_consumption'].value || '0';
    }

    addressChange(event: AddressExt | null, podId?: string) {
        if (podId) {
            const podAddress = mkMemberAddress(event);
            this.podAddresses[podId] = podAddress;
            this.addressPodFormControl(podId).setValue(googleCompatibleTextAddress(podAddress));
        } else {
            const address = mkMemberAddress(event);
            this.memberAddress = address;
            this.addressFormControl.setValue(googleCompatibleTextAddress(address));
        }
    }

    public foreignCountry(): boolean {

        if (isDefined(this.memberAddress)) {
            if (this.memberAddress.region === null && this.memberAddress.country !== null) {
                // custom address case we do not know if the address has a foreing country or not
                // we assume it to be in Italy
                return false;
            }
            return isDefined(this.memberAddress.country) &&
                !ALLOWED_COUNTRIES.includes(this.memberAddress.country);
        }

        return false;
    }

    isNextEnabled(stepName: string) {  // Help me stepName, I'm stuck
        switch (stepName) {
            case PERSONAL_DATA:
                return this.isPersonalDataPageValid;
            case YOUR_UTILITIES:
                return this.isYourUtilitiesPageValid;
            case ENERGY_PROFILING:
                return this.isEnergyProfilingPageValid;
            case UPLOAD_DOCUMENTS:
                return this.validateDocuments();
            default:
                return false;
        }
    }

    isControlNotOK(controlName: string, form: FormGroup) {
        return form.controls[controlName].dirty && !form.controls[controlName].valid && form.controls[controlName].enabled;
    }

    getPodError(controlName: string): string {
        let err = null;
        const control = this.utilitiesForm.controls[controlName];
        if (control.dirty && control.hasError('unique_pod')) {
            err = 'unique_pod';
        } else if (control.dirty && control.hasError('duplicate_pods')) {
            err = 'duplicate_pods';
        } else if (control.dirty && control.invalid) {
            err = 'invalid';
        }
        return err;
    }

    getPodCode(podId: string) {
        return this.utilitiesForm.controls['pod_' + podId + '_code'].value;
    }

    nextStep(target: HTMLElement) {
        this.currentStep++;
        target.scrollIntoView();
    }

    prevStep(target: HTMLElement) {
        this.currentStep--;
        target.scrollIntoView();
    }

    onChangePodEnergyProfile(pod: string, $event: MatRadioChange) {
        // hide POD utility type for producers
        if ($event.value === PreMemberPodEnergyProfile.PRODUCER) {
            this.utilitiesForm.controls[`pod_${pod}_pod_utility_type`].setValue(null);
            this.utilitiesForm.controls[`pod_${pod}_pod_utility_type`].disable();
        } else {
            this.utilitiesForm.controls[`pod_${pod}_pod_utility_type`].enable();
        }
        // hide phases/voltage for PODs with no plants and fall-back to defaults
        if (!this.hasPlant($event.value)) {
            this.utilitiesForm.controls[`pod_${pod}_phases`].setValue(DEFAULT_PHASES);
            this.utilitiesForm.controls[`pod_${pod}_voltage`].setValue(DEFAULT_VOLTAGE);
        }
    }

    onChangePlantType(pod: string, $event: MatSelectChange) {
        if (!this.plantHasEquivalentHours(pod)) {
            this.utilitiesForm.controls[`pod_${pod}_yearly_equivalent_hours`].setValue(null);
            this.utilitiesForm.controls[`pod_${pod}_yearly_equivalent_hours`].markAsPristine();
            this.utilitiesForm.controls[`pod_${pod}_yearly_equivalent_hours`].disable();
        } else {
            this.utilitiesForm.controls[`pod_${pod}_yearly_equivalent_hours`].enable();
        }
    }

    onDownloadDocumentTemplate(doc: PreMemberDocumentType) {
        const req = this.getFormData();
        const podIndex = doc.pod ? req.utilities.findIndex(pod => pod.pod === doc.pod) : undefined;
        this.api.downloadDocumentTemplate(doc.template_id, req, podIndex)
            .subscribe(([data, filename]) => {
                downloadBlob(data, filename);
            });
    }

    onClickRemoveDocument(doc: string, input: HTMLInputElement) {
        delete this.tmpDocuments[doc];
        input.value = '';
    }

    onUploadDocumentFile(docId: string, event: Event) {
        const files = 'files';
        if (event.target[files] && event.target[files].length) {
            const file: File = event.target[files][0];
            if (this.validateFile(file)) {
                this.tmpDocuments[docId] = {
                    // cap the filename to the backend column length (preserving the extension)
                    fileName: limitFileName(file.name),
                    file
                };
            }
        } else {
            this.tmpDocuments[docId] = {
                fileName: null,
                file: null
            };
        }
    }

    uploadedFileName(docId: string): string {
        return this.tmpDocuments[docId]?.fileName;
    }

    addAnotherPod() {
        const newPodId = this.generatePODId();
        this.pods.push(newPodId);
        this.addPodControls(newPodId);
        this.managePodAddressLikeMemberChange(newPodId);
        if (this.STEPS.includes(ENERGY_PROFILING)) {  // The following are only needed if the profilation step exists
            this.managePodTypeChange(newPodId);
            this.manageYearlyConsumptionFetching();
        }
    }

    onClickRemovePOD(podId: string) {
        this.pods.splice(this.pods.indexOf(podId), 1);
        this.utilitiesForm.removeControl(`pod_${podId}_code`);
        this.utilitiesForm.removeControl(`pod_${podId}_pod_utility_type`);
        this.utilitiesForm.removeControl(`pod_${podId}_address`);
        this.utilitiesForm.removeControl(`pod_${podId}_wifi`);
        this.utilitiesForm.removeControl(`pod_${podId}_provider`);
        this.utilitiesForm.removeControl(`pod_${podId}_plant_nominal_power`);
        this.utilitiesForm.removeControl(`pod_${podId}_yearly_equivalent_hours`);
        this.utilitiesForm.removeControl(`pod_${podId}_plant_type`);
        this.utilitiesForm.removeControl(`pod_${podId}_phases`);
        this.utilitiesForm.removeControl(`pod_${podId}_voltage`);
        this.utilitiesForm.removeControl(`pod_${podId}_plant_effective_date`);
        this.utilitiesForm.removeControl(`pod_${podId}_plant_not_yet_active`);
        this.utilitiesForm.removeControl(`pod_${podId}_address_like_member`);
        this.utilitiesForm.removeControl(`pod_${podId}_plant_incentive`);
        this.utilitiesForm.removeControl(`pod_${podId}_plant_has_incentive`);
        this.utilitiesForm.removeControl(`pod_${podId}_energy_profile`);
        if (this.STEPS.includes(ENERGY_PROFILING)) {
            this.profilationsForm.removeControl(`pod_${podId}_people`);
            this.profilationsForm.removeControl(`pod_${podId}_tv`);
            this.profilationsForm.removeControl(`pod_${podId}_pc`);
            this.profilationsForm.removeControl(`pod_${podId}_ac`);
            this.profilationsForm.removeControl(`pod_${podId}_dishwasher`);
            this.profilationsForm.removeControl(`pod_${podId}_heater`);
            this.profilationsForm.removeControl(`pod_${podId}_custom_range`);
            this.profilationsForm.removeControl(`pod_${podId}_yearly_consumption`);
            this.profilationsForm.removeControl(`pod_${podId}_estimated_yearly_consumption`);
            this.profilationsForm.removeControl(`pod_${podId}_second_part`);
        }
    }

    // @PoweredByAi(date="2026-07-17", model="claude-opus-4-8")
    // ETT-1388: upload documents separately (staged) and then submit the small form referencing them,
    // to avoid oversized requests (413). If any document upload or the form submission fails, the whole
    // candidatura fails (all-or-nothing); staged documents are cleaned up server-side. The staging token
    // is reused across retries so a failed submission does not force re-uploading documents that already
    // landed (only changed files are re-sent; the server overwrites them by document type).
    async sendIt() {
        const spinner = this.openSpinner('Sending request');
        const req: PreMemberRequest = this.getFormData();
        const docsToUpload = this.docTypes
            .filter(d => req.personal_data.docs.map(dc => dc.document_type).includes(d.document_type))
            .filter(d => !!this.tmpDocuments[d.document_type])
            .map(d => ({
                documentType: d.document_type,
                file: this.tmpDocuments[d.document_type].file,
                fileName: this.tmpDocuments[d.document_type].fileName
            }));
        try {
            if (!this.stagingToken) {
                this.stagingToken = await this.api.initApplicationStaging();
                this.stagedDocuments = {};
            }
            for (const doc of docsToUpload) {
                // skip documents already staged under this token (same File reference)
                if (this.stagedDocuments[doc.documentType] === doc.file) {
                    continue;
                }
                try {
                    await this.api.uploadApplicationDocument(this.stagingToken, doc.documentType, doc.file, doc.fileName);
                    this.stagedDocuments[doc.documentType] = doc.file;
                } catch (uploadErr) {
                    throw new DocumentUploadError(doc.fileName, uploadErr);
                }
            }
            await this.api.sendApplicationRequest(req, this.stagingToken);
            // build the recap payload from the current state *before* the forms are reset below
            const recap = this.mkEmailRecap(req);
            spinner.context.next({
                loading: false,
                loadingMessage: this.i18n.translate('Request sent')
            });
            this.currentStep = 0;
            this.resetAllForms();
            this.tmpDocuments = {};
            this.stagingToken = null;
            this.stagedDocuments = {};
            // email recap is best-effort and must not affect the submission outcome
            this.api.sendEmailRecap(recap).catch(recapErr =>
                this.msgBoxService.error(this.i18n.translate('An error occurred while sending the request'), recapErr, false));
            this.router.navigate(['/c', 'application']).then();
        } catch (err) {
            const cause = err instanceof DocumentUploadError ? err.cause : err;
            // if the server rejected the staging token as invalid/expired, drop it so a retry starts fresh
            if (errorCode(cause) === PREMEMBER_ERROR_CODES.INVALID_STAGING_TOKEN) {
                this.stagingToken = null;
                this.stagedDocuments = {};
            }
            // ETT-1417: the backend flags a rejected email with the neutral INVALID_EMAIL code (e.g. it
            // already exists) on the form submission; show the email-specific message. Document upload
            // failures (DocumentUploadError) always take the upload-specific message instead.
            const isInvalidEmail = errorCode(cause) === PREMEMBER_ERROR_CODES.INVALID_EMAIL;
            const errMsg = err instanceof DocumentUploadError
                ? this.i18n.translate('An error occurred while uploading document {0}', err.fileName)
                : this.i18n.translate(isInvalidEmail ? 'Invalid e-mail' : 'An error occurred while sending the request');
            spinner.context.next({
                loading: false,
                loadingMessage: errMsg,
                abort: true
            });
            this.msgBoxService.error(errMsg, cause);
        }
    }

    translateRangeFor(range: string): string {
        const startIndex = range.search(/[a-zA-Z]/);
        if (startIndex === -1) {
            return range;
        }
        return range.substring(0, startIndex) + this.i18n.translate(range.substring(startIndex));
    }

    dontHideFirstOption(podId: string): boolean {
        return this.ecmLinked &&
            (this.utilitiesForm.controls['pod_' + podId + '_pod_utility_type'].value !== ECPodUtilityType.RELIGIOUS_INSTITUTION &&
                this.utilitiesForm.controls['pod_' + podId + '_pod_utility_type'].value !== ECPodUtilityType.THIRD_SECTOR_ORGANIZATION_OR_ENVIRONMENT_PROTECTION_ORGANIZATION &&
                this.utilitiesForm.controls['pod_' + podId + '_pod_utility_type'].value !== ECPodUtilityType.OTHER);
    }

    getDocName(docCode: string): string {
        switch (docCode) {
            case EC_DOCUMENT_CI:
                return 'Identity document';
            case EC_DOCUMENT_CF:
                return 'Fiscal code';
            default:
                return docCode;
        }
    }

    plantHasDate(pod: string): boolean {
        return !!this.utilitiesForm.controls['pod_' + pod + '_plant_effective_date'].value;
    }

    onClickResetPlantDate(pod: string) {
        this.utilitiesForm.controls['pod_' + pod + '_plant_effective_date'].setValue(null);
        this.utilitiesForm.controls['pod_' + pod + '_plant_not_yet_active'].enable();
    }

    onClickSetPlantDate(pod: string) {
        const today = moment.tz(DEFAULT_TIMEZONE).startOf('day');
        this.dialogService.open<any, Moment>({
            title: this.i18n.translate('Date of commissioning of the plant'),
            template: this.setPlantDateDialog,
            context: {
                date: new FormControl(today, [Validators.required]) // use "now" as a start
            }
        }, {
            width: 900
        }).closed.then(effectiveDate => {
            if (effectiveDate) {
                this.utilitiesForm.controls['pod_' + pod + '_plant_effective_date'].setValue(effectiveDate.startOf('day'));
                this.utilitiesForm.controls['pod_' + pod + '_plant_effective_date'].disable();
                this.utilitiesForm.controls['pod_' + pod + '_plant_not_yet_active'].setValue(false);
                this.utilitiesForm.controls['pod_' + pod + '_plant_not_yet_active'].disable();
            }
        });
    }

    onClickPlantHasIncentive(pod: string, $event: MatCheckboxChange) {
        if (!$event.checked) {
            this.utilitiesForm.controls['pod_' + pod + '_plant_incentive'].setValue(null);
            this.utilitiesForm.controls['pod_' + pod + '_plant_incentive'].markAsPristine();
        }
    }

    getPodUtilityTypes(juridicalType: ECJuridicalType, legalForm: ECLegalForm, subjectType: ECSubjectType,
                       entitySubtype: ECEntitySubtype): ECPodUtilityType[] {
        if (juridicalType === ECJuridicalType.INDIVIDUAL) {
            return PHYSICAL_POD_UTILITY_TYPES[legalForm];
        } else if (juridicalType === ECJuridicalType.CORPORATE_ENTITY) {
            if (subjectType === ECSubjectType.TERRITORIAL_BODY_LOCAL_AUTHORITY_OTHER_BODY) {
                if (entitySubtype) {
                    return (JURIDICAL_POD_UTILITY_TYPES[subjectType])[entitySubtype];
                }
                return [ECPodUtilityType.OTHER];  // return OTHER if we have no entity subtype set
            }
            return JURIDICAL_POD_UTILITY_TYPES[subjectType];
        } else {
            console.error('juridical type not selected');
            return [];
        }
    }

    openDocument(documentType: string) {
        this.api.getLegalDoc(documentType)
            .then(blob => window.open(URL.createObjectURL(blob), '_blank'))
            .catch(err => console.log((err)));
    }

    hasPlant(profile: PreMemberPodEnergyProfile): boolean {
        return profile === PreMemberPodEnergyProfile.PROSUMER || profile === PreMemberPodEnergyProfile.PRODUCER;
    }

    plantHasEquivalentHours(pod: string): boolean {
        // yearly equivalent hours is only needed for some type of plants
        const plantType = this.utilitiesForm.controls[`pod_${pod}_plant_type`].value;
        return plantType === UtilityProductionType.WIND;
    }

    private resetAllForms() {
        if (this.personalDataForm) {
            this.personalDataForm.reset();
            this.personalDataForm.controls.subjectType.setValue(ECMemberType.NATURAL_PERSON);
            if (this.instanceCommunities) {
                this.personalDataForm.controls.preferredCommunity.setValue(this.communityFromUrl ? this.communityFromUrl : this.instanceCommunities[0]);
            }
        }
        if (this.utilitiesForm) {
            this.utilitiesForm.reset();
            this.pods.forEach(podId => {
                this.addPodControls(podId);
            });
        }
        if (this.profilationsForm) {
            this.profilationsForm.reset();
        }
        if (this.legalDocumentsForm) {
            this.legalDocumentsForm.reset();
        }
    }

    private getFieldCfg(key: AllFieldTypes): PresentField | undefined {
        return !!this.presentFields ? this.presentFields.find(pf => pf.type === key) : undefined;
    }

    private setMissingFieldsConfigurability() {
        this.configurabilitySteps = {  // field name (string): is_present (boolean)
            stepPersonalData: {
                subject_type: this.getFieldCfg(AllFieldTypes.SUBJECT_TYPE),
                first_name: this.getFieldCfg(AllFieldTypes.FIRST_NAME),
                last_name: this.getFieldCfg(AllFieldTypes.LAST_NAME),
                company_name: this.getFieldCfg(AllFieldTypes.COMPANY_NAME),
                address: this.getFieldCfg(AllFieldTypes.ADDRESS),
                tax_id: this.getFieldCfg(AllFieldTypes.TAX_ID),
                email: this.getFieldCfg(AllFieldTypes.EMAIL),
                pec: this.getFieldCfg(AllFieldTypes.PEC),
                piva: this.getFieldCfg(AllFieldTypes.PIVA),
                mobile_number: this.getFieldCfg(AllFieldTypes.MOBILE_NUMBER)
            },
            stepUtilities: {
                pod: this.getFieldCfg(AllFieldTypes.POD),
                pod_utility_type: this.getFieldCfg(AllFieldTypes.POD_UTILITY_TYPE),
                pod_address: this.getFieldCfg(AllFieldTypes.POD_ADDRESS),
                wifi: this.getFieldCfg(AllFieldTypes.WIFI),
                energy_profile: this.getFieldCfg(AllFieldTypes.ENERGY_PROFILE),
                utility_production_type: this.getFieldCfg(AllFieldTypes.UTILITY_PRODUCTION_TYPE),
                plant_nominal_power: this.getFieldCfg(AllFieldTypes.PLANT_NOMINAL_POWER),
                plant_effective_date: this.getFieldCfg(AllFieldTypes.PLANT_EFFECTIVE_DATE),
                provider: this.getFieldCfg(AllFieldTypes.PROVIDER),
                yearly_equivalent_hours: this.getFieldCfg(AllFieldTypes.YEARLY_EQUIVALENT_HOURS),
                phases: this.getFieldCfg(AllFieldTypes.PHASES),
                voltage: this.getFieldCfg(AllFieldTypes.VOLTAGE),
                plant_incentive: this.getFieldCfg(AllFieldTypes.PLANT_INCENTIVE)
            }
        };
        this.updateLimitHeight();
    }

    private getProfilationsControlsValueChanges(): Observable<any>[] {
        return Object.keys(this.profilationsForm.controls)  // all controls relevant to profilations
            .filter(ctrlStr => ctrlStr.endsWith('_people') ||
                ctrlStr.endsWith('_tv') ||
                ctrlStr.endsWith('_pc') ||
                ctrlStr.endsWith('_ac') ||
                ctrlStr.endsWith('_dishwasher') ||
                ctrlStr.endsWith('_heater') ||
                ctrlStr.endsWith('_custom_range'))
            .map(ctrlStr => this.profilationsForm.controls[ctrlStr].valueChanges.pipe(map((val: string) => {
                return {podId: this.extractPodIdFromCtrlStr(ctrlStr), value: val};
            })));
    }

    private extractPodIdFromCtrlStr(str: string): string {
        return str.split('_')[1];  // given the ctrl form of "pod_podId_attribute"
    }

    private manageYearlyConsumptionFetching() {
        merge(...this.getProfilationsControlsValueChanges())
            .pipe(debounceTime(250), takeUntil(this.destroyed))
            .subscribe((temp: { podId: string, value: string }) => {  // value isn't even relevant
                const util = this.mkTempUtil(temp.podId);
                this.updateYearlyConsumption(util, temp.podId);
            });
    }

    private updateYearlyConsumption(util: PreMemberUtility, podId: string) {
        if (!!util.profiling) {
            this.api.updateYearlyConsumption(util)
                .then(yearlyCons => this.profilationsForm.controls['pod_' + podId + '_estimated_yearly_consumption'].setValue(yearlyCons));
        } else {
            // producer POD
            this.profilationsForm.controls['pod_' + podId + '_estimated_yearly_consumption'].setValue(null);
        }
    }

    private mkTempUtil(podId: string): PreMemberUtility {
        const energyProfile = this.utilitiesForm.controls['pod_' + podId + '_energy_profile'].value;
        return {
            pod: 'temp_' + podId,
            profiling: energyProfile !== PreMemberPodEnergyProfile.PRODUCER ? {
                yearly_consumption: null,
                profile_custom_range: this.profilationsForm.controls['pod_' + podId + '_custom_range'].value,
                people: this.profilationsForm.controls['pod_' + podId + '_people'].value,
                tv: this.profilationsForm.controls['pod_' + podId + '_tv'].value,
                pc: this.profilationsForm.controls['pod_' + podId + '_pc'].value,
                ac: this.profilationsForm.controls['pod_' + podId + '_ac'].value,
                dishwasher: this.profilationsForm.controls['pod_' + podId + '_dishwasher'].value,
                heater: this.profilationsForm.controls['pod_' + podId + '_heater'].value
            } : null,
            pod_utility_type: this.utilitiesForm.controls['pod_' + podId + '_pod_utility_type'].value,
            address: null,
            custom_address: null,
            energy_profile: energyProfile,
            collocated: null,
            has_wifi: null,
            zone: this.podAddresses[podId] ? getZoneFromProvince(this.podAddresses[podId].province) : null
        };
    }

    private managePodAddressLikeMemberChange(podId: string) {
        this.utilitiesForm.controls['pod_' + podId + '_address_like_member']
            .valueChanges.pipe(takeUntil(this.destroyed))
            .subscribe(value => {
                    if (value) {
                        this.addressPodFormControl(podId).disable();
                        this.addressPodFormControl(podId).clearValidators();  // This is not enough
                        this.addressPodFormControl(podId).setValue(this.addressFormControl.value);
                        this.podAddresses[podId] = this.memberAddress;
                    } else {
                        this.addressPodFormControl(podId).enable();
                        this.addressPodFormControl(podId).setValidators([Validators.required]);
                        this.addressPodFormControl(podId).setValue(null);
                        delete this.podAddresses[podId];
                    }
                }
            );
    }

    private managePodTypeChange(podId: string) {
        this.utilitiesForm.controls['pod_' + podId + '_pod_utility_type'].valueChanges.pipe(takeUntil(this.destroyed)).subscribe(
            () => {
                this.profilationsForm.controls['pod_' + podId + '_estimated_yearly_consumption'].setValue(null);
            }
        );
    }

    private mkEmailRecap(req: PreMemberRequest): NionMessage {
        return {
            recipient: req.personal_data.email,
            params: {
                firstName: req.personal_data.first_name,
                lastName: req.personal_data.last_name,
                subjectType: req.personal_data.subject_type ?
                    this.i18n.translate(this.subjectTypePipe.transform(req.personal_data.subject_type)) : null,
                legalForm: req.personal_data.legal_form ?
                    this.i18n.translate(this.legalFormPipe.transform(req.personal_data.legal_form)) : null,
                companyName: req.personal_data.company_name || null,
                taxCode: req.personal_data.tax_code || null,
                address: this.addressIsMissing(req.personal_data.address) ? null : req.personal_data.address,
                email: req.personal_data.email,
                mobileNumber: req.personal_data.mobile_number || null,
                docs: req.personal_data.docs.map(d => this.docToNeon(this.getDocName(d.document_type))) || null,
                utilities: req.utilities.map(u => this.utilityToNeon(u)),
                preferredCommunity: this.getCommunityForRecap(req.preferred_community?.name)
            }
        };
    }

    private utilityToNeon(u: PreMemberUtility) {
        return {  // those are the fields the SNS template expects, you have to keep those aligned
            pod: u.pod,
            podUtilityType: u.pod_utility_type ? this.pipe.transform(u.pod_utility_type) : null, // no POD utility type for producers
            address: this.addressIsMissing(u.address) ? null : u.address,
            hasWifi: u.has_wifi,
            profiling: {
                people: u.profiling?.people,
                profileCustomRange: u.profiling?.profile_custom_range,
                totalConsumption: u.profiling?.yearly_consumption
            }
        };
    }

    // This happens when configurability removes the address field
    private addressIsMissing(address: ECMemberAddress): boolean {
        if (!address) {
            return true;
        }
        return !address.province;
    }

    private getCommunityForRecap(name: string) {
        // If no instance communities are found, then return null
        // otherwise, return the preference name or "no preference"
        if (this.instanceCommunities) {
            return name || this.i18n.translate('No preference');
        } else {
            return null;
        }
    }

    private getPreferredCommunity(pref: PreMemberCommunity) {
        // If no instance communities are found or the selected value is "0" (no_preference), then return null
        // otherwise, return the preference
        if (this.instanceCommunities) {
            return (pref.id !== '0') ? pref : null;
        } else {
            return null;
        }
    }

    private docToNeon(docName: string) {
        // SNS expects an object having the "name" attribute
        return {
            name: this.pipe.transform(docName)
        };
    }

    private getFormData(): PreMemberRequest {
        return {
            personal_data: {
                juridical_type: this.personalDataForm.controls.juridicalType.value,
                legal_form: this.personalDataForm.controls.legalForm.value,
                subject_type: this.personalDataForm.controls.subjectType.value,
                entity_subtype: this.personalDataForm.controls.entitySubtype.value,
                email: this.personalDataForm.controls.email.value,
                pec: this.personalDataForm.controls.pec ? this.personalDataForm.controls.pec.value : null,
                company_name: this.personalDataForm.controls.companyName ? this.personalDataForm.controls.companyName.value : null,
                piva: this.personalDataForm.controls.vatNumber ? this.personalDataForm.controls.vatNumber.value : null,
                tax_code: this.personalDataForm.controls.taxCode ? this.personalDataForm.controls.taxCode.value : null,
                first_name: this.personalDataForm.controls.firstName.value,
                last_name: this.personalDataForm.controls.lastName.value,
                address: this.memberAddress,
                custom_address: null,
                mobile_number: this.personalDataForm.controls.mobile.value,
                docs: this.getDocuments(),
                legal_documents: this.getLegalDocuments()
            },
            utilities: this.getUtilities(),
            policy: {
                rules: true,
                privacy: true
            },
            preferred_community: this.getPreferredCommunity(this.personalDataForm.controls.preferredCommunity.value),
            other: this.utilitiesForm.controls.other.value || null
        };
    }

    private getDocuments(): PreMemberDocument[] {
        const docs: PreMemberDocument[] = [];
        this.docTypes.forEach(doc => {
            // IF file is not present for current doc, do nothing
            if (!this.tmpDocuments[doc.document_type]?.fileName || !this.tmpDocuments[doc.document_type]?.file) {
                return;
            }
            const d: PreMemberDocument = {
                document_type: doc.document_type,
                data: null,
                filename: this.tmpDocuments[doc.document_type]?.fileName
            };
            docs.push(d);
        });
        return docs;
    }

    private getLegalDocuments(): PreMemberLegalDocument[] {
        const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone ?? DEFAULT_TIMEZONE;
        const ts = moment.tz(timeZone).valueOf();
        return Object.entries(this.legalDocumentsForm.controls)
            .map(([id, control]) => ({id, ts, acc: !!control.value}));
    }

    private getUtilities(): PreMemberUtility[] {
        const pods: PreMemberUtility[] = [];
        this.pods.forEach(pod => {
            const energyProfile = this.utilitiesForm.controls[`pod_${pod}_energy_profile`].value;
            const p: PreMemberUtility = {
                pod: this.utilitiesForm.controls[`pod_${pod}_code`].value,
                pod_utility_type: this.utilitiesForm.controls[`pod_${pod}_pod_utility_type`].value,
                address: this.podAddresses.hasOwnProperty(pod) ? this.podAddresses[pod] : this.utilitiesForm.controls[`pod_${pod}_address`].value,
                custom_address: null,
                has_wifi: this.utilitiesForm.controls[`pod_${pod}_wifi`].value,
                energy_profile: energyProfile,
                production_type: this.utilitiesForm.controls[`pod_${pod}_plant_type`].value,
                profiling: energyProfile !== PreMemberPodEnergyProfile.PRODUCER ? this.getProfiling(pod) : null,
                plant_nominal_power: this.sanityReplaceFloat(this.utilitiesForm.controls[`pod_${pod}_plant_nominal_power`].value),
                yearly_equivalent_hours: this.sanityReplaceFloat(this.utilitiesForm.controls[`pod_${pod}_yearly_equivalent_hours`].value),
                phases: this.utilitiesForm.controls[`pod_${pod}_phases`].value,
                voltage_level: this.utilitiesForm.controls[`pod_${pod}_voltage`].value,
                energy_provider: this.utilitiesForm.controls[`pod_${pod}_provider`].value,
                collocated: false,
                plant_effective_date: this.utilitiesForm.controls[`pod_${pod}_plant_effective_date`].value ?
                    this.utilitiesForm.controls[`pod_${pod}_plant_effective_date`].value.valueOf() : null,
                plant_not_yet_active: this.utilitiesForm.controls[`pod_${pod}_plant_effective_date`].value ? false :
                    (this.utilitiesForm.controls[`pod_${pod}_plant_not_yet_active`].value ?? false),
                plant_incentivated: this.utilitiesForm.controls[`pod_${pod}_plant_has_incentive`].value,
                plant_incentivation: !!this.utilitiesForm.controls[`pod_${pod}_plant_has_incentive`].value ? this.utilitiesForm.controls[`pod_${pod}_plant_incentive`].value || null : null
            };
            pods.push(p);
        });
        return pods;
    }

    private getProfiling(pod: string): PreMemberProfiling {
        // If profilation steps isn't included, send null values for everything
        if (!this.STEPS.includes(ENERGY_PROFILING)) {
            return {
                yearly_consumption: null,
                profile_custom_range: null,
                people: null,
                tv: null,
                pc: null,
                ac: null,
                dishwasher: null,
                heater: null
            };
        }
        if (!this.secondPartPodValue(pod)) {  // we send estimated yearly cons and profile custom range
            return {
                yearly_consumption: this.profilationsForm.controls[`pod_${pod}_estimated_yearly_consumption`].value || null,
                profile_custom_range: this.profilationsForm.controls[`pod_${pod}_custom_range`].value,
                people: null,
                tv: null,
                pc: null,
                ac: null,
                dishwasher: null,
                heater: null
            };
        } else {  // we inputted yearly consumption
            return {
                yearly_consumption: this.sanityReplaceFloat(this.profilationsForm.controls[`pod_${pod}_yearly_consumption`].value || null),
                profile_custom_range: null,
                people: null,
                tv: null,
                pc: null,
                ac: null,
                dishwasher: null,
                heater: null
            };
        }
    }

    private sanityReplaceFloat(val: string): number {
        return val ? +val.replace(',', '.') : null;
    }

    private generatePODId(): string {
        const podIds = getPODIds(this.utilitiesForm.controls);
        if (!podIds.length) {
            return '1';
        }
        const newId = podIds.map(podId => parseInt(podId, 10))
            .reduce((pv, cv) => Math.max(pv, cv), 0) + 1;
        return newId.toString(10);
    }

    private addPodControls(podId: string) {
        this.utilitiesForm.addControl(`pod_${podId}_code`, new FormControl(null,
            this.configurabilitySteps.stepUtilities.pod ?
                [validateIfMandatory(this.configurabilitySteps.stepUtilities.pod, validateNotBlank), Validators.pattern(POD_PATTERN), Validators.maxLength(POD_MAX_CHARS)] :
                [],
            [ctrl => this.validatePOD(ctrl)]));
        this.utilitiesForm.addControl(`pod_${podId}_energy_profile`, new FormControl(null,
            this.configurabilitySteps.stepUtilities.energy_profile ? [validateIfMandatory(this.configurabilitySteps.stepUtilities.energy_profile, Validators.required)] : []));
        this.utilitiesForm.addControl(`pod_${podId}_pod_utility_type`, new FormControl(null,
            this.configurabilitySteps.stepUtilities.pod_utility_type ? [validateIfMandatory(this.configurabilitySteps.stepUtilities.pod_utility_type, this.mkPodUtilityTypeValidator(podId))] : []));
        this.utilitiesForm.addControl(`pod_${podId}_address`, new FormControl(null,
            this.configurabilitySteps.stepUtilities.pod_address ? [validateIfMandatory(this.configurabilitySteps.stepUtilities.pod_address, Validators.required), validateAddressStringLength(() => this.podAddresses.hasOwnProperty(podId), ADDRESS_FIELD_MAX_CHARS)] : [validateAddressStringLength(() => this.podAddresses.hasOwnProperty(podId), ADDRESS_FIELD_MAX_CHARS)]));
        this.utilitiesForm.addControl(`pod_${podId}_wifi`, new FormControl(this.configurabilitySteps.stepUtilities.wifi ? false : null,
            this.configurabilitySteps.stepUtilities.wifi ? [validateIfMandatory(this.configurabilitySteps.stepUtilities.wifi, Validators.required)] : []));
        this.utilitiesForm.addControl(`pod_${podId}_provider`, new FormControl(null,
            this.configurabilitySteps.stepUtilities.provider ? [validateIfMandatory(this.configurabilitySteps.stepUtilities.provider, validateNotBlank), Validators.pattern(PROVIDER_PATTERN), Validators.maxLength(DEFAULT_MAX_CHARS)] : []));
        this.utilitiesForm.addControl(`pod_${podId}_plant_type`, new FormControl(null,
            this.configurabilitySteps.stepUtilities.utility_production_type ? [validateIfMandatory(this.configurabilitySteps.stepUtilities.utility_production_type, Validators.required)] : []));
        this.utilitiesForm.addControl(`pod_${podId}_plant_nominal_power`, new FormControl(null,
            this.configurabilitySteps.stepUtilities.plant_nominal_power ?
                [validateIfMandatory(this.configurabilitySteps.stepUtilities.plant_nominal_power, Validators.required), ctrl => validateFloat(ctrl, this.i18n.language, 0), Validators.maxLength(DEFAULT_NUMBER_MAX_CHARS)] :
                [Validators.maxLength(DEFAULT_NUMBER_MAX_CHARS)]));
        this.utilitiesForm.addControl(`pod_${podId}_yearly_equivalent_hours`, new FormControl(null,
            this.configurabilitySteps.stepUtilities.yearly_equivalent_hours ? [validateIfMandatory(this.configurabilitySteps.stepUtilities.yearly_equivalent_hours, this.mkYearlyEquivalentHoursValidator(podId)), ctrl => validateFloat(ctrl, this.i18n.language, 0)] : []));
        this.utilitiesForm.addControl(`pod_${podId}_phases`, new FormControl(DEFAULT_PHASES,
            this.configurabilitySteps.stepUtilities.phases ? [validateIfMandatory(this.configurabilitySteps.stepUtilities.phases, Validators.required)] : []));
        this.utilitiesForm.addControl(`pod_${podId}_voltage`, new FormControl(DEFAULT_VOLTAGE,
            this.configurabilitySteps.stepUtilities.voltage ? [validateIfMandatory(this.configurabilitySteps.stepUtilities.voltage, Validators.required)] : []));
        this.utilitiesForm.addControl(`pod_${podId}_plant_not_yet_active`, new FormControl(false, []));
        this.utilitiesForm.addControl(`pod_${podId}_plant_effective_date`, new FormControl(null, []));
        this.utilitiesForm.addControl(`pod_${podId}_address_like_member`, new FormControl({value: false, disabled: this.foreignCountry()}, []));
        this.utilitiesForm.addControl(`pod_${podId}_plant_has_incentive`, new FormControl(null, []));
        this.utilitiesForm.addControl(`pod_${podId}_plant_incentive`, new FormControl(null,
            this.configurabilitySteps.stepUtilities.plant_incentive ? [validateIfMandatory(this.configurabilitySteps.stepUtilities.plant_incentive, this.mkPlantIncentiveValidator(podId)), Validators.maxLength(DEFAULT_MAX_CHARS)] : []));
        if (this.STEPS.includes(ENERGY_PROFILING)) {
            this.profilationsForm.addControl(`pod_${podId}_people`, new FormControl(null, [validateInteger]));
            this.profilationsForm.addControl(`pod_${podId}_tv`, new FormControl(null, [validateInteger]));
            this.profilationsForm.addControl(`pod_${podId}_pc`, new FormControl(null, [validateInteger]));
            this.profilationsForm.addControl(`pod_${podId}_ac`, new FormControl(null, [validateInteger]));
            this.profilationsForm.addControl(`pod_${podId}_dishwasher`, new FormControl(false, []));
            this.profilationsForm.addControl(`pod_${podId}_heater`, new FormControl(false, []));
            this.profilationsForm.addControl(`pod_${podId}_custom_range`, new FormControl(null, []));
            this.profilationsForm.addControl(`pod_${podId}_yearly_consumption`, new FormControl(null, [ctrl => validateFloat(ctrl, this.i18n.language, 0)]));
            this.profilationsForm.addControl(`pod_${podId}_estimated_yearly_consumption`, new FormControl(null, []));
            this.profilationsForm.addControl(`pod_${podId}_second_part`, new FormControl(!this.ecmLinked, []));  // ECM not linked means we default to second radio btn
        }
    }

    private validateFile(file: File): boolean {
        if (!FILE_MIME_TYPES.find(mt => mt.type === file.type)) {
            this.msgBoxService.error(this.i18n.translate('File {0} is not a {1} file', file.name, this.mimeTypesNames));
            return false;
        }
        if (file.size > MAX_FILE_MB * 1048576) {
            this.msgBoxService.error(this.i18n.translate('Size of file {0} must be below {1} MB', file.name, MAX_FILE_MB));
            return false;
        }
        return true;
    }

    private onChangeResetPodsVals() {
        if (this.pods.length > 0) {
            this.pods.forEach(podId => {
                if (this.STEPS.includes(ENERGY_PROFILING)) {
                    this.profilationsForm.controls['pod_' + podId + '_custom_range'].reset();
                }
                this.utilitiesForm.controls['pod_' + podId + '_pod_utility_type'].reset();  // this also resets estimated yearly cons
            });
        }
    }

    private onChangeSubjectType(selectedJuridicalType: ECJuridicalType) {
        this.personalDataForm.controls.legalForm.reset();
        this.personalDataForm.controls.entitySubtype.reset();
        switch (selectedJuridicalType) {
            case ECJuridicalType.CORPORATE_ENTITY:
                this.personalDataForm.controls.taxCode.reset();
                this.personalDataForm.controls.legalForm.clearValidators();
                this.personalDataForm.controls.legalForm.updateValueAndValidity({emitEvent: false});
                this.personalDataForm.controls.subjectType.setValidators([Validators.required]);
                this.personalDataForm.controls.subjectType.updateValueAndValidity({emitEvent: false});
                break;
            case ECJuridicalType.INDIVIDUAL:
                this.personalDataForm.controls.companyName.reset();
                this.personalDataForm.controls.vatNumber.reset();
                this.personalDataForm.controls.legalForm.setValidators([Validators.required]);
                this.personalDataForm.controls.legalForm.updateValueAndValidity({emitEvent: false});
                this.personalDataForm.controls.subjectType.clearValidators();
                this.personalDataForm.controls.subjectType.updateValueAndValidity({emitEvent: false});
                break;
            default:
                break;
        }
        // manage height limit in personal data form
        this.updateLimitHeight();
    }

    private updateLimitHeight() {
        this.turboMaxHeight = Object.entries(this.configurabilitySteps.stepPersonalData).filter(([key, value]) => {
            if (!this.isCompany) {
                return value && key !== AllFieldTypes.COMPANY_NAME && key !== AllFieldTypes.PIVA;
            } else {  // private and public institution
                return value && key !== AllFieldTypes.TAX_ID;
            }
        }).length;
        // Add one to the counter if email is present (since we also have "confirm email step")
        if (this.configurabilitySteps.stepPersonalData.email) {
            this.turboMaxHeight += 1;
        }
        // Add one to the counter if address is present
        if (this.configurabilitySteps.stepPersonalData.address) {
            this.turboMaxHeight += 1;
        }
        if (this.personalDataForm?.controls.subjectType?.value === ECSubjectType.TERRITORIAL_BODY_LOCAL_AUTHORITY_OTHER_BODY) {
            this.turboMaxHeight += 1;
        }
    }

    private validateForm(ctrl: AbstractControl): ValidationErrors | null {
        const form = ctrl as FormGroup;
        // Check both emails are the same
        if (form.controls.email.dirty &&
            form.controls.confirmEmail.dirty &&
            (form.controls.email.value !== form.controls.confirmEmail.value)) {
            return {different_emails: true};
        }
        return null;
    }

    private validateDocuments(): boolean {
        // check if every mandatory document has been uploaded
        return this.docTypes
            .filter(dt => !dt.optional)
            .map(dt => dt.document_type)
            .reduce((res, key) => res && !!this.tmpDocuments[key], true);
    }

    private openSpinner(message: string) {
        const spinnerData = new BehaviorSubject<SpinnerData>({
            loading: true,
            loadingMessage: this.i18n.translate(message)
        });
        return this.dialogService.open<Subject<SpinnerData>, undefined>({
            template: this.spinnerDialog,
            context: spinnerData
        }, {
            width: DIALOG_WIDTH,
            hideCloseButton: true
        });
    }

    private validateYearlyConsumption(podId: string): boolean {
        const podEnergyProfile: PreMemberPodEnergyProfile = this.utilitiesForm.controls[`pod_${podId}_energy_profile`].value;
        if (podEnergyProfile === PreMemberPodEnergyProfile.PRODUCER) {
            // no validation needed for producers, as they do not have a consumption profile at all
            return true;
        }
        if (!this.energyProfilingMandatory) {
            // no checks for presence of yearly consumption needed
            return true;
        }
        if (this.secondPartPodValue(podId)) {
            // yearly consumption explicitly given
            const yearlyConsumptionCtrl = this.profilationsForm.controls[`pod_${podId}_yearly_consumption`];
            return !Validators.required(yearlyConsumptionCtrl) && !yearlyConsumptionCtrl.invalid;
        } else {
            // yearly consumption estimated from other parameters (appliances, custom range, ...)
            const estimatedYearlyConsumptionCtrl = this.profilationsForm.controls[`pod_${podId}_estimated_yearly_consumption`];
            return !Validators.required(estimatedYearlyConsumptionCtrl) && !estimatedYearlyConsumptionCtrl.invalid;
        }
    }

    private validatePOD(ctrl: AbstractControl): Observable<{ [err: string]: boolean }> {
        const pod = mkTextField(ctrl.value);
        if (!pod) {
            return of(null);
        }
        // First reject PODs duplicated within the form itself.
        const duplicate = this.areDuplicatePodsInserted(pod);
        if (duplicate) {
            return of(duplicate);
        }
        // Then reject PODs that already have an application on record, tested against the reference
        // bloom filter (mirrors the email uniqueness check).
        return timer(250).pipe(
            switchMap(() => this.getReference().then(filters => filters.p)),
            map(filter => filter.has(CryptoJS.MD5(pod).toString())),
            map(unique_pod => unique_pod ? {unique_pod} : null));
    }

    private areDuplicatePodsInserted(pod: string): { [err: string]: boolean } {
        this.podsIds = this.pods.map(id => this.utilitiesForm.controls[`pod_${id}_code`].value);
        // If next condition is met, it means we have duplicate values inside our 'this.pods'
        const singletonPods = new Set(this.podsIds);
        if (singletonPods.size !== this.podsIds.length && this.podsIds.filter(id => id === pod).length > 1) {
            return {duplicate_pods: true};
        }
        if (singletonPods.size === this.podsIds.length) {
            this.pods.map(id => {
                this.utilitiesForm.controls[`pod_${id}_code`].setErrors(clearErrors(this.utilitiesForm.controls[`pod_${id}_code`].errors, 'duplicate_pods'));
            });
        }
        return null;
    }

    private updateDocTypes() {
        this.docTypes = mkDocumentTypes({
                juridical_type: this.personalDataForm?.controls?.juridicalType?.value,
                legal_form: this.personalDataForm?.controls?.legalForm?.value,
                subject_type: this.personalDataForm?.controls?.subjectType?.value,
                entity_subtype: this.personalDataForm?.controls?.entitySubtype?.value
            } as PreMemberPersonalData,
            this.pods.map(pod => ({
                pod: this.utilitiesForm?.controls[`pod_${pod}_code`]?.value,
                pod_utility_type: this.utilitiesForm?.controls[`pod_${pod}_pod_utility_type`]?.value,
                energy_profile: this.utilitiesForm?.controls[`pod_${pod}_energy_profile`]?.value
            } as PreMemberUtility)),
            this.docSpecs);
    }

    private validatePlantEffectiveDate(podId: string): boolean {
        if (this.configurabilitySteps?.stepUtilities.plant_effective_date) {
            const effectiveDate = this.utilitiesForm.controls['pod_' + podId + '_plant_effective_date'].value;
            const notYetActive = this.utilitiesForm.controls['pod_' + podId + '_plant_not_yet_active'].value;
            if (this.configurabilitySteps.stepUtilities.plant_effective_date.mandatory) {
                return !!effectiveDate || notYetActive;
            } else {
                return !effectiveDate || !notYetActive;
            }
        }
        return true;
    }

    private mkPodUtilityTypeValidator(podId: string): ValidatorFn {
        return ctrl => {
            // POD utility type is not needed for producers
            const energyProfile = this.utilitiesForm.controls[`pod_${podId}_energy_profile`].value;
            if (energyProfile !== PreMemberPodEnergyProfile.PRODUCER) {
                return Validators.required(ctrl);
            }
            return null;
        };
    }

    private mkYearlyEquivalentHoursValidator(podId: string): ValidatorFn {
        return ctrl => {
            const podEnergyProfile = this.utilitiesForm.controls[`pod_${podId}_energy_profile`].value;
            if (this.hasPlant(podEnergyProfile) && this.plantHasEquivalentHours(podId)) {
                return Validators.required(ctrl);
            }
            return null;
        };
    }

    private mkPlantIncentiveValidator(podId: string): ValidatorFn {
        return ctrl => {
            // plant incentive is only mandatory if "has incentive" flag is checked
            const plantHasIncentive = !!this.utilitiesForm.controls[`pod_${podId}_plant_has_incentive`].value;
            if (plantHasIncentive) {
                return validateNotBlank(ctrl);
            }
            return null;
        };
    }

    private emailAsyncValidator(): AsyncValidatorFn {
        return (control: AbstractControl): Observable<ValidationErrors | null> => {
            if (!control.value) return of(null);
            return timer(250).pipe(
                switchMap(() => this.getReference().then(filters => filters.e)),
                map(filter => filter.has(CryptoJS.MD5(control.value).toString())),
                map(checkFailed => checkFailed ? {checkFailed} : null));
        };
    }

    private async storeReference() {
        const referenceFilters = await this.api.getReferenceFilters().toPromise();
        this.storageService.storeSession('ecm-reference', referenceFilters);
        return referenceFilters;
    }

    private async getReference() {
        let data: ReferenceFilters = this.storageService.loadSession('ecm-reference');
        if (!data || data.ts < (Date.now() - environment.referenceCacheTTL)) { // cache is invalid
            data = await this.storeReference();
        }
        return {...data, e: BloomFilter.fromJSON(data.e), p: BloomFilter.fromJSON(data.p)};
    }

}


function getPODIds(ctrls: { [ctrl: string]: AbstractControl }): string[] {
    return Object.keys(ctrls)
        .filter(name => name.startsWith('pod_')
            && name.endsWith('_code')
            && !name.endsWith('postal_code'))
        .map(name => name.substring(4, name.length - 5));
}

function mkTextField(value: string): string {
    const tv = value ? value.trim() : '';
    return tv.length ? tv : undefined;
}

function mkDocumentTypes(personalData: PreMemberPersonalData, utilities: PreMemberUtility[], docSpecs: PreMemberDocumentSpec[]): PreMemberDocumentType[] {
    const docTypes: Set<string> = new Set<string>();
    const documents: PreMemberDocumentType[] = [];

    docSpecs.forEach(doc => {
        const juridicalTypes = doc.subject_juridical_types || [];
        const legalForms = doc.subject_legal_forms || [];
        const subjectTypes = doc.subject_types || [];
        const subjectEntitySubtypes = doc.subject_entity_subtypes || [];
        const podEnergyProfiles = doc.pod_energy_profiles || [];
        const podUtilityTypes = doc.pod_utility_types || [];

        // check subject
        if (!!personalData.juridical_type && !!juridicalTypes.length && juridicalTypes.indexOf(personalData.juridical_type) < 0)
            return;
        if (!!personalData.legal_form && !!legalForms.length && legalForms.indexOf(personalData.legal_form) < 0)
            return;
        if (!!personalData.subject_type && !!subjectTypes.length && subjectTypes.indexOf(personalData.subject_type) < 0)
            return;
        if (!!personalData.entity_subtype && !!subjectEntitySubtypes.length && subjectEntitySubtypes.indexOf(personalData.entity_subtype) < 0)
            return;

        const utilityFilter: (utility: PreMemberUtility) => boolean = utility => {
            if (!!utility.energy_profile && !!podEnergyProfiles.length && podEnergyProfiles.indexOf(mkEnergyProfileForDocument(utility.energy_profile)) < 0)
                return false;
            if (!!utility.pod_utility_type && !!podUtilityTypes.length && podUtilityTypes.indexOf(utility.pod_utility_type) < 0)
                return false;
            return true;
        };

        // check PODs (at least one must be present)
        if (!utilities.find(utility => utilityFilter(utility))) return;

        // at most *one* document for each type is returned
        if (docTypes.has(doc.document_type)) {
            return;
        }
        docTypes.add(doc.document_type);

        if (doc.replicable_per_pod) {
            utilities.filter(utility => utilityFilter(utility))
                .forEach(utility => {
                    documents.push({
                        document_type: doc.document_type.replace(REPLICA_POD_DOC_PLACEHOLDER, utility.pod),
                        optional: doc.optional,
                        pod: utility.pod,
                        template_id: doc.template_id
                    });
                });
        } else {
            documents.push({
                document_type: doc.document_type,
                optional: doc.optional,
                pod: null,
                template_id: doc.template_id
            });
        }
    });

    return documents;
}

function mkEnergyProfileForDocument(profile: PreMemberPodEnergyProfile): PreMemberPodEnergyProfile {
    if (profile === PreMemberPodEnergyProfile.ASPIRING_PROSUMER) {
        return PreMemberPodEnergyProfile.CONSUMER;
    }
    return profile;
}

function validateIfMandatory(fieldCfg: PresentField, validator: ValidatorFn): ValidatorFn {
    return fieldCfg.mandatory ? validator : (ctrl => null);
}
